import { Button, Checkbox, Input, Select, Steps } from 'antd'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import FadeIn from 'src/components/styled/FadeIn'
import {
  GroupHero,
  GroupLead,
  GroupPage,
  GroupSurface,
  GroupTitle,
} from 'src/screens/venue-groups/venueGroupChrome'
import { API } from 'src/utils/interceptor'
import { canManageAnyVenue } from 'src/utils/adminAccess'
import { filterVenuesForGroupFill } from 'src/screens/venues/venueListFilters'
import { fetchEveryVenuePage } from 'src/screens/venues/venueListPages'
import {
  RejectedNotification,
  SuccessNotification,
  WarningNotification,
} from 'src/notifications/SuccessNotification'

const { Step } = Steps

export const CREATE_WIZARD_STEPS = [
  { title: '1. Name' },
  { title: '2. Venues' },
  { title: '3. Review' },
]

export const selectAllowedVenueMatches = (selected, matches, visible) => {
  const visibleIds = visible
    ? new Set(visible.map(venue => String(venue._id)))
    : null
  const ids = new Set(selected.map(venue => String(venue._id)))
  const next = [...selected]
  ;(matches || []).forEach(venue => {
    if (!venue?.canChange || ids.has(String(venue._id))) return
    if (visibleIds && !visibleIds.has(String(venue._id))) return
    ids.add(String(venue._id))
    next.push(venue)
  })
  return next
}

export const venueReviewLine = venue => {
  const town = [venue?.addressCity, venue?.addressPostCode]
    .filter(Boolean)
    .join(' ')
  return [town, venue?.brand, venue?.owner].filter(Boolean).join(' · ')
}

const createReviewPlanLabel = venue => {
  const pricing = venue?.newPricing || {}
  if (pricing.pro?.isSubscribed) return 'Pro'
  if (pricing.growth?.isSubscribed) return 'Growth'
  if (pricing.starter?.isSubscribed) return 'Starter'
  return 'Free'
}

const CREATE_REVIEW_PLAN_ORDER = ['Free', 'Starter', 'Growth', 'Pro']

const CREATE_REVIEW_ACTIVITY_ORDER = [
  ['green', 'active'],
  ['amber', 'dormant'],
  ['red', 'inactive'],
  ['purple', 'never ordered'],
]

/** Review step summary before Save: “5 venues · 2 Growth · 1 active · 3 never ordered”. */
export const createReviewMixLine = venues => {
  const list = venues || []
  const total = list.length
  const parts = [total === 1 ? '1 venue' : `${total} venues`]
  CREATE_REVIEW_PLAN_ORDER.forEach(label => {
    const count = list.filter(
      venue => createReviewPlanLabel(venue) === label,
    ).length
    if (count) parts.push(`${count} ${label}`)
  })
  CREATE_REVIEW_ACTIVITY_ORDER.forEach(([color, label]) => {
    const count = list.filter(venue => venue?.activityColor === color).length
    if (count) parts.push(`${count} ${label}`)
  })
  return parts.join(' · ')
}

export const CREATE_VENUE_PAGE_SIZE = 10

export const pageOfVenues = (
  venues,
  page,
  pageSize = CREATE_VENUE_PAGE_SIZE,
) => {
  const list = venues || []
  const pageCount = Math.max(1, Math.ceil(list.length / pageSize))
  const safe = Math.min(Math.max(1, page || 1), pageCount)
  const start = list.length === 0 ? 0 : (safe - 1) * pageSize + 1
  const end = Math.min(safe * pageSize, list.length)
  return {
    page: safe,
    pageCount,
    start,
    end,
    rows: list.slice((safe - 1) * pageSize, safe * pageSize),
  }
}

export const matchPastedVenueNames = (venues, pasted) => {
  const seen = new Set()
  const names = []
  String(pasted || '')
    .split(/[\n,]/)
    .forEach(item => {
      const name = String(item || '').trim()
      const key = name.toLowerCase()
      if (!key || seen.has(key)) return
      seen.add(key)
      names.push(name)
    })
  const matched = []
  const unmatched = []
  const ambiguous = []
  names.forEach(name => {
    const hits = (venues || []).filter(
      venue =>
        String(venue.title || '')
          .trim()
          .toLowerCase() === name.toLowerCase(),
    )
    if (hits.length === 0) unmatched.push(name)
    else if (hits.length === 1) matched.push(hits[0])
    else
      ambiguous.push({
        name: hits[0].title || name,
        count: hits.length,
        venues: hits,
      })
  })
  return { matched, unmatched, ambiguous }
}

export const ambiguousPasteLines = groups =>
  (groups || []).map(
    group =>
      `${group.count} venues are called ${group.name}. Tick the ones you mean.`,
  )

export const venuesForPasteJump = ({ matched, ambiguous } = {}) => {
  const seen = new Set()
  const rows = []
  ;[
    ...(matched || []),
    ...(ambiguous || []).flatMap(group => group.venues || []),
  ].forEach(venue => {
    const id = String(venue?._id || '')
    if (!id || seen.has(id)) return
    seen.add(id)
    rows.push(venue)
  })
  return rows
}

export const pasteSelectionFeedback = ({
  selectedCount,
  visibleOnPageCount,
} = {}) => {
  const selected = Number(selectedCount) || 0
  if (!selected) return ''
  const onPage = Number(visibleOnPageCount) || 0
  if (onPage < selected) return `Selected ${selected}, including off this page`
  return `Selected ${selected}`
}

const pickerVenue = venue => ({
  ...venue,
  canChange: venue.canChange !== false,
})

const venuesMatchingName = (venues, query) => {
  const needle = String(query || '')
    .trim()
    .toLowerCase()
  if (!needle) return venues || []
  return (venues || []).filter(venue =>
    String(venue.title || '')
      .toLowerCase()
      .includes(needle),
  )
}

const VenueGroupCreate = () => {
  const history = useHistory()
  const account = useSelector(state => state.auth.account)
  const username = useSelector(state => state.auth.cognito?.username)
  const isOmsAdmin = account?.isAdmin === true
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [query, setQuery] = useState('')
  const [plan, setPlan] = useState('all')
  const [brand, setBrand] = useState('all')
  const [activity, setActivity] = useState('all')
  const [page, setPage] = useState(1)
  const [catalogue, setCatalogue] = useState([])
  const [brands, setBrands] = useState([])
  const [loadingVenues, setLoadingVenues] = useState(false)
  const [pasted, setPasted] = useState('')
  const [unmatched, setUnmatched] = useState([])
  const [selected, setSelected] = useState([])
  const [saving, setSaving] = useState(false)
  const [customerVisible, setCustomerVisible] = useState(true)
  const [nameWarning, setNameWarning] = useState('')
  const [outsideList, setOutsideList] = useState([])
  const [ambiguousLines, setAmbiguousLines] = useState([])
  const [chooseFrom, setChooseFrom] = useState([])
  const [pasteFeedback, setPasteFeedback] = useState('')

  useEffect(() => {
    if (!canManageAnyVenue(account)) history.replace('/venues')
  }, [account, history])

  useEffect(() => {
    if (step !== 1 || !username) return undefined
    let cancelled = false
    setLoadingVenues(true)
    const catalogueLoad = Promise.all([
      fetchEveryVenuePage({ username, get: API.get }),
      API.get('api2', 'venue-groups/venue-brands'),
    ])
      .then(([list, brandRows]) => {
        if (cancelled) return null
        setCatalogue((list || []).map(pickerVenue))
        setBrands(brandRows?.data || [])
        return null
      })
      .catch(() => {
        if (!cancelled) setCatalogue([])
        return null
      })
      .finally(() => {
        if (!cancelled) setLoadingVenues(false)
      })
    catalogueLoad.catch(() => null)
    return () => {
      cancelled = true
    }
  }, [step, username])

  useEffect(() => {
    setPage(1)
    setChooseFrom([])
    setAmbiguousLines([])
    setPasteFeedback('')
  }, [query, plan, brand, activity])

  useEffect(() => {
    if (step !== 2) return undefined
    let cancelled = false
    const trimmed = name.trim()
    if (!trimmed) {
      setNameWarning('')
      return undefined
    }
    API.get(
      'api2',
      `venue-groups/name-check?name=${encodeURIComponent(trimmed)}`,
    )
      .then(response => {
        if (!cancelled) setNameWarning(response?.warning || '')
        return null
      })
      .catch(() => {
        if (!cancelled) setNameWarning('')
        return null
      })
    return () => {
      cancelled = true
    }
  }, [name, step])

  const detailById = new Map(
    (brands || []).map(venue => [String(venue._id), venue]),
  )
  const brandById = new Map(
    (brands || []).map(venue => [String(venue._id), venue.brand || '']),
  )
  const reviewVenue = venue => {
    const extra = detailById.get(String(venue._id)) || {}
    return {
      ...venue,
      addressCity: venue.addressCity || extra.addressCity,
      addressPostCode: venue.addressPostCode || extra.addressPostCode,
      brand: extra.brand || '',
      owner: extra.owner || '',
    }
  }
  const filtered = venuesMatchingName(
    filterVenuesForGroupFill(catalogue, {
      plan,
      brand: isOmsAdmin ? brand : 'all',
      activity,
      brands: brandById,
    }),
    query,
  )
  const listed = chooseFrom.length ? chooseFrom : filtered
  const venuePage = pageOfVenues(listed, page)

  const findPasted = () => {
    const names = pasted.trim()
    if (!names) return
    const everywhere = matchPastedVenueNames(catalogue, names)
    const inView = matchPastedVenueNames(filtered, names)
    const allowedMatched = (inView.matched || []).filter(
      venue => venue.canChange !== false,
    )
    const jumpTo = venuesForPasteJump({
      matched: allowedMatched,
      ambiguous: inView.ambiguous,
    })
    const pageRows = pageOfVenues(jumpTo, 1).rows
    const visibleIds = new Set(pageRows.map(venue => String(venue._id)))
    const onPage = allowedMatched.filter(venue =>
      visibleIds.has(String(venue._id)),
    ).length
    setUnmatched(everywhere.unmatched)
    setOutsideList(
      inView.unmatched.filter(name => !everywhere.unmatched.includes(name)),
    )
    setAmbiguousLines(ambiguousPasteLines(inView.ambiguous))
    setChooseFrom(jumpTo)
    setPage(1)
    setPasteFeedback(
      pasteSelectionFeedback({
        selectedCount: allowedMatched.length,
        visibleOnPageCount: onPage,
      }),
    )
    setSelected(current =>
      selectAllowedVenueMatches(current, inView.matched, inView.matched),
    )
  }

  const toggle = venue => {
    if (!venue.canChange) return
    setSelected(current =>
      current.some(item => item._id === venue._id)
        ? current.filter(item => item._id !== venue._id)
        : [...current, venue],
    )
  }

  const save = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    if (selected.length === 0) return
    setSaving(true)
    try {
      const response = await API.post('api2', 'venue-groups', {
        body: {
          name: trimmed,
          venueIds: selected.map(venue => venue._id),
          ...(isOmsAdmin && !customerVisible ? { customerVisible: false } : {}),
        },
      })
      SuccessNotification({ message: 'Group created.' })
      if (response?.warning) WarningNotification({ message: response.warning })
      const groupId = response?.data?._id
      history.push(groupId ? `/venue-groups/${groupId}` : '/venue-groups')
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message || 'Could not create this group.',
      })
    } finally {
      setSaving(false)
    }
  }

  if (!canManageAnyVenue(account)) return null

  return (
    <FadeIn>
      <GroupPage>
        <GroupHero>
          <GroupTitle>Create a group</GroupTitle>
          <GroupLead>
            Name it, choose venues you can manage, then save. You can copy into
            them after. Other admins only see the sites they manage in this
            group.
          </GroupLead>
        </GroupHero>
        <GroupSurface>
          <CreateSteps current={step} size='small'>
            {CREATE_WIZARD_STEPS.map(item => (
              <Step key={item.title} title={item.title} />
            ))}
          </CreateSteps>
          {step === 0 && (
            <Section>
              <label htmlFor='group-name'>Name</label>
              <Input
                id='group-name'
                value={name}
                maxLength={80}
                onChange={event => setName(event.target.value)}
                placeholder='Group name'
              />
              {isOmsAdmin ? (
                <Checkbox
                  checked={!customerVisible}
                  onChange={event => setCustomerVisible(!event.target.checked)}
                  style={{ marginTop: 12 }}
                >
                  OMS only — hide from customers
                </Checkbox>
              ) : null}
            </Section>
          )}
          {step === 1 && (
            <Section>
              <Reason>
                You can add any venue you can manage, including one under a
                different owner. A venue you cannot change is left out.
              </Reason>
              <FilterRow>
                <FilterField label='Name' $grow>
                  <Input
                    id='venue-search'
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    placeholder='Name contains'
                  />
                </FilterField>
                <FilterField label='Plan'>
                  <Select
                    value={plan}
                    onChange={setPlan}
                    style={{ width: 140 }}
                  >
                    <Select.Option value='all'>All plans</Select.Option>
                    <Select.Option value='Free Plan'>Free</Select.Option>
                    <Select.Option value='Starter Plan'>Starter</Select.Option>
                    <Select.Option value='Growth Plan'>Growth</Select.Option>
                    <Select.Option value='Pro Plan'>Pro</Select.Option>
                  </Select>
                </FilterField>
                {isOmsAdmin && (
                  <FilterField label='Brand'>
                    <Select
                      value={brand}
                      onChange={setBrand}
                      style={{ width: 140 }}
                    >
                      <Select.Option value='all'>All brands</Select.Option>
                      <Select.Option value='TFR'>TFR</Select.Option>
                      <Select.Option value='GREEN'>GREEN</Select.Option>
                      <Select.Option value='JUST'>JUST</Select.Option>
                    </Select>
                  </FilterField>
                )}
                <FilterField label='Activity'>
                  <Select
                    value={activity}
                    onChange={setActivity}
                    style={{ width: 180 }}
                    dropdownMatchSelectWidth={false}
                  >
                    <Select.Option value='all'>All venues</Select.Option>
                    <Select.Option value='green'>
                      Active — last order within 30 days
                    </Select.Option>
                    <Select.Option value='amber'>
                      Dormant — last order 31 to 365 days ago
                    </Select.Option>
                    <Select.Option value='red'>
                      Inactive — last order over a year ago
                    </Select.Option>
                    <Select.Option value='purple'>Never ordered</Select.Option>
                  </Select>
                </FilterField>
              </FilterRow>
              <label htmlFor='venue-paste'>Paste a list of names</label>
              <Input.TextArea
                id='venue-paste'
                value={pasted}
                rows={4}
                onChange={event => setPasted(event.target.value)}
                placeholder={'One venue name per line'}
              />
              <Button onClick={findPasted} disabled={!pasted.trim()}>
                Select matching venues
              </Button>
              {pasteFeedback ? <Reason>{pasteFeedback}</Reason> : null}
              {unmatched.length > 0 && (
                <Reason>Not found: {unmatched.join(', ')}</Reason>
              )}
              {outsideList.length > 0 && (
                <Reason>
                  Not in this list: {outsideList.join(', ')}. Clear the filters
                  to search the rest.
                </Reason>
              )}
              {ambiguousLines.map(line => (
                <Reason key={line}>{line}</Reason>
              ))}
              {loadingVenues && <Reason>Loading venues…</Reason>}
              {!loadingVenues && (
                <Reason>
                  {listed.length === 0
                    ? '0 venues.'
                    : `${listed.length} ${
                        listed.length === 1 ? 'venue' : 'venues'
                      }. Showing ${venuePage.start}–${venuePage.end}.`}
                </Reason>
              )}
              {!loadingVenues && venuePage.pageCount > 1 && (
                <Actions>
                  <Button
                    disabled={venuePage.page <= 1}
                    onClick={() => setPage(current => current - 1)}
                  >
                    Previous page
                  </Button>
                  <Button
                    disabled={venuePage.page >= venuePage.pageCount}
                    onClick={() => setPage(current => current + 1)}
                  >
                    Next page
                  </Button>
                </Actions>
              )}
              <VenueList>
                {venuePage.rows.map(venue => (
                  <VenueRow key={venue._id}>
                    <label>
                      <input
                        type='checkbox'
                        checked={selected.some(
                          item => String(item._id) === String(venue._id),
                        )}
                        disabled={!venue.canChange}
                        onChange={() => toggle(venue)}
                      />
                      {venue.title}
                    </label>
                    {!venue.canChange && <Reason>{venue.reason}</Reason>}
                  </VenueRow>
                ))}
              </VenueList>
              {!loadingVenues && selected.length === 0 ? (
                <Reason>Select at least one venue to continue.</Reason>
              ) : null}
            </Section>
          )}
          {step === 2 && (
            <Section>
              <ReviewName>{name.trim()}</ReviewName>
              {nameWarning ? <Reason>{nameWarning}</Reason> : null}
              {selected.length === 0 ? (
                <p>
                  Choose at least one venue before saving. An empty group cannot
                  be created.
                </p>
              ) : (
                <>
                  <ReviewMix>{createReviewMixLine(selected)}</ReviewMix>
                  <p>These venues will be saved in this group.</p>
                  <VenueList>
                    {selected.map(venue => {
                      const line = venueReviewLine(reviewVenue(venue))
                      return (
                        <VenueRow key={venue._id}>
                          <div>
                            <div>{venue.title}</div>
                            {line ? <Reason>{line}</Reason> : null}
                          </div>
                        </VenueRow>
                      )
                    })}
                  </VenueList>
                </>
              )}
            </Section>
          )}
          <Actions>
            <Button
              onClick={() =>
                step === 0 ? history.push('/venue-groups') : setStep(step - 1)
              }
            >
              Back
            </Button>
            {step < 2 ? (
              <Button
                type='primary'
                disabled={
                  (step === 0 && !name.trim()) ||
                  (step === 1 && selected.length === 0)
                }
                onClick={() => setStep(step + 1)}
              >
                Continue
              </Button>
            ) : (
              <Button
                type='primary'
                loading={saving}
                disabled={!name.trim() || selected.length === 0}
                onClick={save}
              >
                Save
              </Button>
            )}
          </Actions>
        </GroupSurface>
      </GroupPage>
    </FadeIn>
  )
}

const CreateSteps = styled(Steps)`
  /* Number lives in the title ("3. Review"); hide the icon digit so it
     cannot smash into the label as "3Review". */
  .ant-steps-item-icon {
    display: none;
  }

  .ant-steps-item-content {
    margin-top: 0;
    min-height: auto;
  }

  .ant-steps-item-title {
    padding-right: 16px;
    line-height: 24px;
  }

  .ant-steps-item-title::after {
    top: 12px;
  }
`

const Section = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const VenueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;

  label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
`

const VenueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid #eef3e4;
  border-radius: 8px;
  background: #fff;
`

const ReviewName = styled.p`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 650;
  color: #132d07;
`

const ReviewMix = styled.p`
  margin: 0;
  color: #5c6560;
  font-size: 14px;
  line-height: 1.45;
`

const Reason = styled.span`
  color: #6b6256;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
`

const FilterRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: flex-end;
`

const FilterFieldBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: ${({ $grow }) => ($grow ? '1 1 12rem' : '0 0 auto')};
  min-width: ${({ $grow }) => ($grow ? '8rem' : 'auto')};
`

const FilterField = ({ label, $grow, children }) => (
  <FilterFieldBox $grow={$grow}>
    <FilterFieldLabel>{label}</FilterFieldLabel>
    {children}
  </FilterFieldBox>
)

const FilterFieldLabel = styled.span`
  color: #5c6560;
  font-size: 12px;
  line-height: 1.2;
`

export default VenueGroupCreate
